import { NavLinkItem } from 'motion-components';
import React, { FC } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { generatePath } from 'react-router-dom';
import { getRouteByName } from './routes';
import { useRootStore } from 'store';
import { t } from 'i18next';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useUserPermissions } from 'shared-pages/hooks/user-permissions';
import { commonMenuItems } from 'shared-pages/common/menu-items';

const MI = {
  CONTACTS: 'Επαφές',
  FILES: 'Αρχεία',
  MATERIALS: 'Απόβλητα',
  CONTAINER_TYPES: 'Περιέκτες / Συσκευασίες',
  REGIONS: 'Περιφέρειες',
  TAXOFFICES: 'Δ.Ο.Υ.',
  SUBREGIONS: 'Περιφερειακές Ενότητες',
  NOMOI: 'Νομοί',
  JOBTYPES: 'Κατηγορίες Εργασιών',
  USER_ROLES: 'Ρόλοι Χρηστών',
  POSTALCODES2: 'Ταχυδρομικοί Κώδικες',
  USERS: 'Χρήστες',
  VEHICLES: 'Οχήματα',
  DRIVERS: 'Οδηγοί',
  EDOC_SERIES: 'Σειρές Αρίθμησης',
  WASTE_RECEPTION_POINTS: 'Ανακυκλωτές',
  WASTE_RECEPTION_POINTS_DA: 'Δευτερογενής Αποθηκεύσεις',
  COLLECTOR: 'Συλλέκτης',
  COLLECTORS: 'Συλλέκτες',
  COLLECTION_POINTS: 'Σημεία Συλλογής',
  COLLECTION_POINT: 'Σημείο Συλλογής',
  COLLECTION_POINT_MAP: 'Χάρτης Σημείων Συλλογής',
  COLLECTION_POINTS_STATS: 'Στατιστικά Σημείων Συλλογής',
  COLLECTION_POINT_CATEGORIES: 'Κατηγορίες Σημείων',
  COLLECTION_POINT_REQUESTS: 'Αιτήσεις Νέων Σημείων',
  CONTAINER_REQUESTS: 'Παραγγελίες Συσκευασιών',
  PRODUCTION: 'Παραγωγή',
  ROUTES: 'Δρομολόγια',
  COLLECTIONS: 'Συλλογές',
  COLLECTIONS_ORDERS: 'Αιτήσεις Συλλογής',
  INTERNALTRANSACTIONS: 'Εσωτερικές Διακινήσεις',
  WASTERECEPTIONPOINT_TRANSACTIONS: 'Κινήσεις Αποθήκης',
  DRIVER_ENTRIES: 'Καταχωρήσεις Οδηγών',
  IDOCS: 'Έντυπα Αναγνώρισης',
  DISP_NOTES: 'Δελτία Αποστολής',
  REPORTS: 'Αναφορές',
  COLLECTION_POINT_REPORT: 'Συλλογές Σημείου Συλλογής',
  COLLECTORS_SUMS_REPORT: 'Σύνολα Συλλεκτών',
  COLLECTORS_RESPONSE_REPORT: 'Απόκριση Συλλεκτών',
  MATERIAL_REPORT: 'Αναφορά Αποβλήτων',
  COLLECTIONS_REP_DGRM: 'Ανάλυση Συλλογών',
  COLLECTIONS_PIVOT_SRV: 'Pivot Συλλογών',
  COLLECTIONS_PIVOT: 'Pivot Συλλογών 0',
  EDOCS: 'Πρωτόκολλο',
  ADMINISTRATION: 'Διαχείριση',
  AUDIT_TRAIL: 'Audit Trail',
  MAILS_REPO: 'Mails',
  LOG_BOOK: 'Log Book',
  USERS_STATS: 'Στατιστικά Χρηστών',
};

//import { t as T } from 'i18next';
// const t = function (str) {
//   console.log(`${str.toUpperCase()}: '${str}'`);
//   return T(str);
// };

const gqlAddContextlog = gql`
  mutation addContextlog($data: ContextlogInput) {
    addContextlog(data: $data) {
      id
    }
  }
`;

type Props = {};

const MyNavLinkItem = ({ text, routeName, disabled = false }) => {
  const { getUserCanView } = useUserPermissions();

  if (typeof routeName === 'object') {
    const rtName = routeName.routeName;
    const to = { ...routeName, pathname: getRouteByName(rtName) };
    const canView = getUserCanView(rtName);
    if (!canView || disabled) {
      return (
        <a className="pl-3 nav-link disabled" style={{ color: 'gray!important' }} href="/">
          {text}
        </a>
      );
    }
    return <NavLinkItem text={text} to={to} enable={canView} />;
  } else {
    const canView = getUserCanView(routeName);
    if (!canView || disabled) {
      return <a className="pl-3 nav-link disabled">{text}</a>;
    }
    return <NavLinkItem text={text} to={getRouteByName(routeName)} enable={canView} />;
  }
};

export const MainMenu: FC<Props> = () => {
  /*-------------------- Body -----------------------------------------------*/
  const { appStore, authStore } = useRootStore();
  const [addContextLog] = useMutation(gqlAddContextlog);

  const mi = {
    ...commonMenuItems,
    _________: <NavDropdown.Divider />,
    Materials: <MyNavLinkItem text={t(MI.MATERIALS)} routeName={'Materials'} />,
    ContainerTypes: <MyNavLinkItem text={t(MI.CONTAINER_TYPES)} routeName={'ContainerTypes'} />,
    Regions: <MyNavLinkItem text={t(MI.REGIONS)} routeName={'Regions'} />,
    Subregions: <MyNavLinkItem text={t(MI.SUBREGIONS)} routeName={'Subregions'} />,
    Taxoffices: <MyNavLinkItem text={t(MI.TAXOFFICES)} routeName={'Taxoffices'} />,
    Nomoi: <MyNavLinkItem text={t(MI.NOMOI)} routeName={'Nomoi'} />,
    Jobtypes: <MyNavLinkItem text={t(MI.JOBTYPES)} routeName={'Jobtypes'} />,
    Postalcodes2: <MyNavLinkItem text={t(MI.POSTALCODES2)} routeName={'Postalcodes2'} />,
    Vehicles: <MyNavLinkItem text={t(MI.VEHICLES)} routeName="Vehicles" />,
    Drivers: <MyNavLinkItem text={t(MI.DRIVERS)} routeName="Drivers" />,
    EdocSeries: <MyNavLinkItem text={t(MI.EDOC_SERIES)} routeName="Series" />,
    Collectors: <MyNavLinkItem text={t(MI.COLLECTORS)} routeName="Collectors" />,
    Contacts: <MyNavLinkItem text={t(MI.CONTACTS)} routeName="Contacts" />,
    WasteReceptionPoints: (
      <MyNavLinkItem text={t(MI.WASTE_RECEPTION_POINTS)} routeName="WasteReceptionPoints" />
    ),
    WasteReceptionPointsDa: (
      <MyNavLinkItem text={t(MI.WASTE_RECEPTION_POINTS_DA)} routeName="WasteReceptionPointsDa" />
    ),
    Users: <NavLinkItem text={t(MI.USERS)} to={getRouteByName('Users')} enable={true} />,
    Production: <MyNavLinkItem text={t(MI.PRODUCTION)} routeName="Productions" />,
    Collectionpoints: (
      <MyNavLinkItem
        text={t(MI.COLLECTION_POINTS)}
        routeName={{ routeName: 'Collectionpoints', state: { app: 'RE-BATTERY' } }}
      />
    ),
    CollectionpointsStats: (
      <MyNavLinkItem
        text={t(MI.COLLECTION_POINTS_STATS)}
        routeName={{ routeName: 'CPStats', state: {} }}
      />
    ),
    CollectionpointsMap: (
      <MyNavLinkItem
        text={t(MI.COLLECTION_POINT_MAP)}
        routeName={{ routeName: 'CPMap', state: { app: 'RE-BATTERY' } }}
        disabled={false}
      />
    ),
    CollectionpointCategories: (
      <MyNavLinkItem text={t(MI.COLLECTION_POINT_CATEGORIES)} routeName={'CpGroups'} />
    ),
    CollectionpointRequests: (
      <MyNavLinkItem text={t(MI.COLLECTION_POINT_REQUESTS)} routeName={'CollectionpointRequests'} />
    ),
    ContainerRequests: (
      <MyNavLinkItem text={t(MI.CONTAINER_REQUESTS)} routeName={'ContainerOrders'} />
    ),
    CollectionOrders: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.COLLECTIONS_ORDERS)}
        routeName={{ routeName: 'Orders', state: { app: 'WBMANAGER' } }}
      />
    ),
    Routes: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.ROUTES)}
        routeName={{ routeName: 'Routes', state: { app: 'WBMANAGER' } }}
      />
    ),
    Collections: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.COLLECTIONS)}
        routeName={{ routeName: 'Collections', state: { app: 'WBMANAGER' } }}
      />
    ),
    InternalTransactions: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.INTERNALTRANSACTIONS)}
        routeName={{ routeName: 'ITrans', state: { app: 'WBMANAGER' } }}
      />
    ),
    WrsTransactions: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.WASTERECEPTIONPOINT_TRANSACTIONS)}
        routeName={{ routeName: 'WrsTrans', state: { app: 'WBMANAGER' } }}
      />
    ),
    DriverEntries: <MyNavLinkItem text={t(MI.DRIVER_ENTRIES)} routeName="DriversEntries" />,
    // DispNotes: (
    //   <MyNavLinkItem
    //     text={t(MI.DISP_NOTES)}
    //     routeName={{ routeName: 'DispNotesGrid', state: { company: 'FOTOKIKLOSI' } }}
    //     disabled={true}
    //   />
    // ),
    Idocs: (
      <MyNavLinkItem
        text={t(MI.IDOCS)}
        routeName={{ routeName: 'Idocs', state: { company: 'FOTOKIKLOSI' } }}
        disabled={false}
      />
    ),
    CollectionPointReport: (
      <MyNavLinkItem
        text={t(MI.COLLECTION_POINT_REPORT)}
        routeName={{ routeName: 'CollectionPointReport', state: { company: 'FOTOKIKLOSI' } }}
      />
    ),
    CollectorsSumsReport: (
      <MyNavLinkItem
        disabled={true}
        text={t(MI.COLLECTORS_SUMS_REPORT)}
        routeName={{ routeName: 'DeliveriesReport', state: { company: 'FOTOKIKLOSI' } }}
      />
    ),
    CollectorsResponseReport: (
      <MyNavLinkItem
        disabled={true}
        text={t(MI.COLLECTORS_RESPONSE_REPORT)}
        routeName={{ routeName: 'DeliveriesReport', state: { company: 'FOTOKIKLOSI' } }}
      />
    ),
    MaterialReport: (
      <MyNavLinkItem
        text={t(MI.MATERIAL_REPORT)}
        routeName={{ routeName: 'MaterialReport', state: { company: 'FOTOKIKLOSI' } }}
      />
    ),
    CollectionsRepDgrm: (
      <MyNavLinkItem
        text={t(MI.COLLECTIONS_REP_DGRM)}
        routeName={{ routeName: 'CollectionsRepDgrm', state: { company: 'FOTOKIKLOSI' } }}
      />
    ),
    PivotTableSrv: (
      <NavLinkItem
        text={t(MI.COLLECTIONS_PIVOT_SRV)}
        to={getRouteByName('CollectionsSrvPivot')}
        enable={true}
      />
    ),
    Edocs: <NavLinkItem text={t(MI.EDOCS)} to={getRouteByName('Edocs')} enable={true} />,
    CollectionsPivotReport: (
      <MyNavLinkItem
        disabled={false}
        text={t(MI.COLLECTIONS_PIVOT)}
        routeName={{ routeName: 'CollectionsPivot', state: { company: '' } }}
      />
    ),
    Audittrail: (
      <NavLinkItem text={t(MI.AUDIT_TRAIL)} to={getRouteByName('AuditTrails')} enable={true} />
    ),
    Mails: <NavLinkItem text={t(MI.MAILS_REPO)} to={getRouteByName('Mails')} enable={true} />,
    // Reports: <NavLinkItem text={t(MI.REPORTS)} to={getRouteByName('Reports')} enable={true} />,
  };

  /* --------------- Platform Menu ------------------- */
  const menuPlatformOwner = () => {
    return (
      <>
        {/****  ΑΡΧΕΙΑ ****/}
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Materials}
          {mi.Jobtypes}
          {mi.ContainerTypes}
          {mi._________}
          {mi.Regions}
          {mi.Subregions}
          {mi.Nomoi}
          {mi.Postalcodes2}
          {mi.Taxoffices}
          {mi._________}
          {mi.Vehicles}
          {mi.Drivers}
          {mi.EdocSeries}
          {mi._________}
          {mi.Collectors}
          {mi.WasteReceptionPoints}
          {mi.WasteReceptionPointsDa}
          {mi._________}
          {mi.Users}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.CollectionpointCategories}
          {mi.Collectionpoints}
          {mi.Contacts}
          {mi._________}
          {mi.CollectionpointsStats}
          {mi.CollectionpointsMap}
          {mi._________}
          {mi.CollectionpointRequests}
          {mi.ContainerRequests}
        </NavDropdown>

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.CollectionOrders}
          {mi.Routes}
          {mi.Collections}
          {mi.InternalTransactions}
          {mi.Production}
          {mi._________}
          {mi.WrsTransactions}

          {mi._________}
          {mi.DriverEntries}
          {mi._________}
          {/* {mi.DispNotes} */}
          {mi.Idocs}
        </NavDropdown>

        {/****  Αναφορές ****/}
        <NavDropdown title={t(MI.REPORTS)} id="collections-nav-dropdown">
          {mi.CollectionPointReport}
          {/* {mi.CollectorsSumsReport} */}
          {/* {mi.CollectorsResponseReport} */}
          {mi.MaterialReport}
          {mi.CollectionsRepDgrm}
          {/* {mi.CollectionsPivotReport} */}
          {mi.PivotTableSrv}
          {mi.Edocs}
        </NavDropdown>

        {/****  Διαχείριση ****/}
        <NavDropdown title={t(MI.ADMINISTRATION)} id="collections-nav-dropdown">
          {mi.Audittrail}
          {mi.Mails}
          {mi.Logbook}
          {mi._________}
          {mi.Params}
          {mi._________}
          {mi.UsersStats}
        </NavDropdown>
      </>
    );
  };

  /* --------------- WasteReceptionPoint Menu ------------------- */
  const menuWasteReceptionPoint = () => {
    return <></>;
  };

  /* --------------- Collection Points Menu ------------------- */
  const menuCollectionpoints = () => {
    return (
      <>
        {mi.Collectionpoints}
        {mi.CollectionOrders}
        {mi.Collections}
        {mi.Idocs}
      </>
    );
  };

  /* --------------- Collectors Menu ------------------- */
  const menuCollectors = () => {
    return (
      <>
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Vehicles}
          {mi.Drivers}
          {mi.EdocSeries}
          {mi._________}
          <NavLinkItem
            text={t(MI.COLLECTOR)}
            to={generatePath(getRouteByName('Collector'), { id: appStore.associatedId ?? 0 })}
            enable={true}
          />
          {mi._________}
          {mi.Users}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.Collectionpoints}
          {mi.CollectionpointsStats}
          {mi.CollectionpointsMap}
          {mi._________}
          {mi.ContainerRequests}
        </NavDropdown>

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.CollectionOrders}
          {mi.Routes}
          {mi.Collections}
          {mi.InternalTransactions}
          {mi.Production}
          {mi._________}
          {mi.WrsTransactions}
          {mi._________}

          {mi.DriverEntries}
          {mi.Idocs}
        </NavDropdown>
      </>
    );
  };

  /* --------------- CollectorDrivers Menu ------------------- */
  const menuCollectorDrivers = () => {
    return (
      <>
        <NavDropdown title={t(MI.FILES)} id="files-nav-dropdown">
          {mi.Vehicles}
        </NavDropdown>

        {/****  Σημεία Συλλογής ****/}
        {/* <NavDropdown title={t(MI.COLLECTION_POINTS)} id="collections-nav-dropdown">
          {mi.Collectionpoints}
          {mi.CollectionpointsMap}
        </NavDropdown> */}

        {/****  Συλλογές ****/}
        <NavDropdown title={t(MI.COLLECTIONS)} id="collections-nav-dropdown">
          {mi.Collections}
          {mi.Routes}
          {mi.DriverEntries}
          {mi.Idocs}
        </NavDropdown>
      </>
    );
  };

  /* --------------- Collectors Help Menu ------------------- */

  const switchMenu = () => {
    if (appStore.isPlatformOwner) return menuPlatformOwner();
    else if (appStore.isCollector) return menuCollectors();
    else if (appStore.isCollectorDriver) return menuCollectorDrivers();
    else if (appStore.isWasteReceptionPoint) return menuWasteReceptionPoint();
    else if (appStore.isCollectionPoint) return menuCollectionpoints();
    else return <></>;
  };

  /*-------------------- Render ---------------------------------------------*/
  return (
    // -- Menu --
    <Nav className="mr-auto">{switchMenu()}</Nav>
  );
};
